import React from "react";
import "./TraficoDesktopWindows.css";
import { WindowManagerContext } from "../../context/WindowManagerContext";

const WINDOW_TYPES = {
    contract: { title: "Contrato Seguimiento", icon: "bx-map-alt" },
};
const MAX_DESKTOP_WINDOWS = 5;
const DEFAULT_WINDOW_SIZE_VERSION = 2;
const MULTIVENTANA_ENABLED_STORAGE_KEY = "trafico_multiventana_enabled";
const isDesktopViewport = () => (window.innerWidth || document.documentElement.clientWidth || 0) >= 1200;

const getContractWindowTitle = (contract, fallbackTitle = "Contrato Seguimiento") => {
    const deviceId = String(contract?.FKLokDeviceID ?? "").trim() || fallbackTitle;
    const tiempo = String(contract?.Tiempo ?? "N/D").trim() || "N/D";
    return `${deviceId} (${tiempo})`;
};

const getDefaultWindowSize = () => {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1280;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;

    const width = Math.min(1220, Math.max(320, viewportWidth - 48));
    const height = Math.min(820, Math.max(220, viewportHeight - 70));
    return { width, height, x: Math.max(12, Math.round((viewportWidth - width) / 2)), y: Math.max(50, Math.round((viewportHeight - height) / 2)) };
};

const getMaximizedWindowSize = () => {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1280;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;

    // Cuando el modal de seguimiento sigue abierto, reutilizamos su geometría
    // real para que la ventana maximizada coincida exactamente con él.
    const parentModal = document.querySelector(".modal.show .modal-dialog");
    const parentBounds = parentModal?.getBoundingClientRect?.();
    if (parentBounds?.width && parentBounds?.height) {
        return {
            width: Math.round(parentBounds.width),
            height: Math.round(parentBounds.height),
            x: Math.round(parentBounds.left),
            y: Math.round(parentBounds.top),
        };
    }

    // Respaldo equivalente al BaseModalXXL (90% de ancho y centrado).
    const width = Math.round(viewportWidth * 0.9);
    const height = Math.min(Math.round(viewportHeight * 0.92), viewportHeight - 48);
    return { width, height, x: Math.round((viewportWidth - width) / 2), y: Math.round((viewportHeight - height) / 2) };
};

class TraficoDesktopWindows extends React.Component {
    static contextType = WindowManagerContext;

    constructor(props) {
        super(props);
        this.state = { windows: [], nextId: 1, nextZIndex: 3000, launcherVisible: isDesktopViewport() && sessionStorage.getItem(MULTIVENTANA_ENABLED_STORAGE_KEY) === "true" };
        this.drag = null;
        this.resize = null;
        this.lastSavedWindowsSignature = null;
    }

    componentWillUnmount() {
        this.stopPointerTracking();
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    componentDidMount() {
        document.addEventListener("keydown", this.handleKeyDown);
        const savedWindows = (this.context?.savedWindows || []).slice(0, MAX_DESKTOP_WINDOWS);
        if (savedWindows.length > 0) {
            const defaultSize = getDefaultWindowSize();
            const restoredWindows = savedWindows.map((window, index) => ({
                ...window,
                ...(!window.maximized && window.defaultSizeVersion !== DEFAULT_WINDOW_SIZE_VERSION ? defaultSize : {}),
                defaultSizeVersion: DEFAULT_WINDOW_SIZE_VERSION,
                title: window.type === "contract"
                    ? getContractWindowTitle(window.contract, window.title)
                    : window.title,
                zIndex: 3001 + index,
                minimized: Boolean(window.minimized),
                maximized: Boolean(window.maximized),
            }));
            this.setState({
                windows: restoredWindows,
                nextId: restoredWindows.length + 1,
                nextZIndex: 3001 + restoredWindows.length,
                launcherVisible: true,
            }, () => this.props.onLauncherVisibilityChange?.(true));
        }
    }

    componentDidUpdate(previousProps, previousState) {
        if (previousState.windows === this.state.windows) return;

        const signature = JSON.stringify(this.state.windows);
        if (signature === this.lastSavedWindowsSignature) return;

        this.lastSavedWindowsSignature = signature;
        this.context?.saveWindows(this.state.windows);
    }

    openWindow = (type = "summary") => {
        if (!isDesktopViewport()) return;
        if (this.state.windows.length >= MAX_DESKTOP_WINDOWS) {
            this.props.onWindowLimitReached?.(MAX_DESKTOP_WINDOWS);
            return;
        }

        const definition = WINDOW_TYPES[type] || WINDOW_TYPES.summary;
        const id = `${type}-${this.state.nextId}`;
        const offset = ((this.state.nextId - 1) % 7) * 28;
        const defaultSize = getDefaultWindowSize();
        this.setState((previous) => ({
            nextId: previous.nextId + 1,
            nextZIndex: previous.nextZIndex + 1,
            windows: [...previous.windows, {
                id,
                type,
                title: definition.title,
                minimized: false,
                zIndex: previous.nextZIndex + 1,
                x: defaultSize.x + offset,
                y: defaultSize.y + offset,
                width: defaultSize.width,
                height: defaultSize.height,
                maximized: false,
                defaultSizeVersion: DEFAULT_WINDOW_SIZE_VERSION,
            }],
        }));
    };

    openContractTab = (contract) => {
        if (!isDesktopViewport()) return false;
        const contractId = String(contract?.ContractID ?? "");
        if (!contractId) return false;
        const existing = this.state.windows.find((window) => String(window.contract?.ContractID) === contractId);
        if (existing) {
            this.setState((previous) => ({ launcherVisible: true, nextZIndex: previous.nextZIndex + 1, windows: previous.windows.map((window) => window.id === existing.id ? { ...window, minimized: false, zIndex: previous.nextZIndex + 1 } : window) }), () => this.props.onLauncherVisibilityChange?.(true));
            return true;
        }
        if (this.state.windows.length >= MAX_DESKTOP_WINDOWS) {
            this.props.onWindowLimitReached?.(MAX_DESKTOP_WINDOWS);
            return false;
        }
        const offset = ((this.state.nextId - 1) % 7) * 28;
        const defaultSize = getDefaultWindowSize();
        this.setState((previous) => ({
            nextId: previous.nextId + 1,
            nextZIndex: previous.nextZIndex + 1,
            launcherVisible: true,
            windows: [...previous.windows, { id: `contract-${contractId}-${previous.nextId}`, type: "contract", title: getContractWindowTitle(contract, contractId), contract, minimized: false, maximized: false, defaultSizeVersion: DEFAULT_WINDOW_SIZE_VERSION, zIndex: previous.nextZIndex + 1, x: defaultSize.x + offset, y: defaultSize.y + offset, width: defaultSize.width, height: defaultSize.height }],
        }), () => this.props.onLauncherVisibilityChange?.(true));
        return true;
    };

    isAtWindowLimit = () => this.state.windows.length >= MAX_DESKTOP_WINDOWS;

    updateContracts = (contracts = []) => {
        if (!Array.isArray(contracts) || contracts.length === 0) return;
        const contractsById = new Map(
            contracts
                .filter((contract) => contract?.ContractID != null)
                .map((contract) => [String(contract.ContractID), contract])
        );

        this.setState((previous) => {
            let changed = false;
            const windows = previous.windows.map((currentWindow) => {
                if (currentWindow.type !== "contract") return currentWindow;
                const updatedContract = contractsById.get(String(currentWindow.contract?.ContractID));
                if (!updatedContract) return currentWindow;

                const nextContract = { ...currentWindow.contract, ...updatedContract };
                const contractChanged = Object.keys(updatedContract).some(
                    (key) => currentWindow.contract?.[key] !== updatedContract[key]
                );
                const nextTitle = getContractWindowTitle(nextContract, currentWindow.title);
                if (!contractChanged && nextTitle === currentWindow.title) return currentWindow;

                changed = true;
                return { ...currentWindow, contract: nextContract, title: nextTitle };
            });

            return changed ? { windows } : null;
        });
    };

    toggleLauncher = () => this.setState((previous) => {
        const launcherVisible = !previous.launcherVisible;
        this.props.onLauncherVisibilityChange?.(launcherVisible);
        return { launcherVisible };
    });

    closeWindow = (id) => this.setState((previous) => {
        const windows = previous.windows.filter((window) => window.id !== id);
        return { windows, launcherVisible: previous.launcherVisible };
    }, () => this.props.onLauncherVisibilityChange?.(this.state.launcherVisible));

    closeAllWindows = () => this.setState((previous) => ({
        windows: [],
        launcherVisible: previous.launcherVisible,
    }), () => this.props.onLauncherVisibilityChange?.(this.state.launcherVisible));

    handleKeyDown = (event) => {
        if (event.key !== "Escape") return;

        // ESC debe seguir disponible para los modales y offcanvas tradicionales.
        if (document.querySelector(".modal.show, .offcanvas.show")) return;

        const openWindows = this.state.windows.filter((window) => !window.minimized);
        const activeWindow = openWindows.reduce(
            (current, window) => (!current || window.zIndex > current.zIndex ? window : current),
            null
        );
        if (!activeWindow) return;

        event.preventDefault();
        this.closeWindow(activeWindow.id);
    };

    minimizeWindow = (id) => this.updateWindow(id, { minimized: true });

    toggleMaximizeWindow = (id) => {
        const currentWindow = this.state.windows.find((window) => window.id === id);
        if (!currentWindow) return;
        const size = currentWindow.maximized ? getDefaultWindowSize() : getMaximizedWindowSize();
        this.updateWindow(id, { ...size, maximized: !currentWindow.maximized });
    };

    restoreWindow = (id) => {
        this.bringToFront(id);
        this.updateWindow(id, { minimized: false });
    };

    bringToFront = (id) => this.setState((previous) => ({
        nextZIndex: previous.nextZIndex + 1,
        windows: previous.windows.map((window) => window.id === id ? { ...window, zIndex: previous.nextZIndex + 1 } : window),
    }));

    updateWindow = (id, changes) => this.setState((previous) => ({ windows: previous.windows.map((window) => window.id === id ? { ...window, ...changes } : window) }));

    startDrag = (event, currentWindow) => {
        if (event.button !== 0) return;
        event.preventDefault();
        this.bringToFront(currentWindow.id);
        this.drag = { id: currentWindow.id, startX: event.clientX, startY: event.clientY, x: currentWindow.x, y: currentWindow.y };
        window.addEventListener("mousemove", this.handleDrag);
        window.addEventListener("mouseup", this.stopPointerTracking, { once: true });
    };

    handleDrag = (event) => {
        if (!this.drag) return;
        const { id, startX, startY, x, y } = this.drag;
        this.updateWindow(id, { x: Math.max(12, x + event.clientX - startX), y: Math.max(70, y + event.clientY - startY) });
    };

    startResize = (event, currentWindow) => {
        event.preventDefault();
        event.stopPropagation();
        this.bringToFront(currentWindow.id);
        this.resize = { id: currentWindow.id, startX: event.clientX, startY: event.clientY, width: currentWindow.width, height: currentWindow.height };
        window.addEventListener("mousemove", this.handleResize);
        window.addEventListener("mouseup", this.stopPointerTracking, { once: true });
    };

    handleResize = (event) => {
        if (!this.resize) return;
        const { id, startX, startY, width, height } = this.resize;
        this.updateWindow(id, { width: Math.max(320, width + event.clientX - startX), height: Math.max(220, height + event.clientY - startY) });
    };

    stopPointerTracking = () => {
        this.drag = null;
        this.resize = null;
        window.removeEventListener("mousemove", this.handleDrag);
        window.removeEventListener("mousemove", this.handleResize);
    };

    renderWindowContent = (currentWindow) => {
        const { filteredContratos = [], counts = {}, onShortcut } = this.props;
        if (currentWindow.type === "contract" && this.props.renderContract) {
            return this.props.renderContract(currentWindow.contract, () => this.closeWindow(currentWindow.id));
        }
        if (currentWindow.type === "alerts") {
            const alerts = [
                ["En ruta", counts.movingCount],
                ["Detenidos", counts.stopCount],
                ["Sin GPS", counts.noGpsCount],
                ["Streaming", counts.streamingCount],
            ];
            return <div className="trafico-window-alert-grid">{alerts.map(([label, value]) => <div key={label}><strong>{value ?? 0}</strong><span>{label}</span></div>)}</div>;
        }
        if (currentWindow.type === "shortcuts") {
            return <div className="trafico-window-shortcuts">
                <button type="button" onClick={() => onShortcut?.("filter")}><i className="bx bx-filter-alt" />Abrir filtros</button>
                <button type="button" onClick={() => onShortcut?.("live")}><i className="bx bx-video" />Transmisiones en vivo</button>
                <button type="button" onClick={() => onShortcut?.("fullscreen")}><i className="bx bx-fullscreen" />Pantalla completa</button>
            </div>;
        }
        return <div className="trafico-window-summary">
            <div><strong>{filteredContratos.length}</strong><span>contratos visibles</span></div>
            <p>Esta ventana permanece dentro de la pestaña actual y puede moverse o redimensionarse.</p>
        </div>;
    };

    render() {
        const minimized = this.state.windows.filter((window) => window.minimized);
        return <>
            {this.state.windows.filter((window) => !window.minimized).map((currentWindow) => (
                <section key={currentWindow.id} className={`trafico-desktop-window ${currentWindow.maximized ? "is-maximized" : ""}`} style={{ left: currentWindow.x, top: currentWindow.y, width: currentWindow.width, height: currentWindow.height, zIndex: currentWindow.zIndex }} onMouseDown={() => this.bringToFront(currentWindow.id)}>
                    <header className="trafico-desktop-window-header" onMouseDown={(event) => this.startDrag(event, currentWindow)}>
                        <h3><i className={`bx ${WINDOW_TYPES[currentWindow.type]?.icon || "bx-window"}`} />{currentWindow.title}</h3>
                        <div>
                            <button type="button" title="Minimizar" onClick={() => this.minimizeWindow(currentWindow.id)}>−</button>
                            <button type="button" title={currentWindow.maximized ? "Restaurar tamaño predeterminado" : "Maximizar"} aria-label={currentWindow.maximized ? "Restaurar tamaño predeterminado" : "Maximizar"} onClick={() => this.toggleMaximizeWindow(currentWindow.id)}><i className={`bx ${currentWindow.maximized ? "bx-window" : "bx-window-alt"}`} aria-hidden="true" /></button>
                            <button type="button" title="Cerrar" onClick={() => this.closeWindow(currentWindow.id)}>×</button>
                        </div>
                    </header>
                    <div className="trafico-desktop-window-body">{this.renderWindowContent(currentWindow)}</div>
                    <button type="button" className="trafico-desktop-window-resize" aria-label="Redimensionar ventana" onMouseDown={(event) => this.startResize(event, currentWindow)} />
                </section>
            ))}
            {this.state.launcherVisible && this.state.windows.length > 0 && <div className="trafico-desktop-minimized-bar">{this.state.windows.map((currentWindow) => <div className={`trafico-desktop-minimized-item ${currentWindow.minimized ? "is-minimized" : "is-open"}`} key={currentWindow.id}><button type="button" onClick={() => currentWindow.minimized ? this.restoreWindow(currentWindow.id) : this.bringToFront(currentWindow.id)}><i className={`bx ${WINDOW_TYPES[currentWindow.type]?.icon || "bx-window"}`} />{currentWindow.title}</button><button type="button" onClick={() => this.closeWindow(currentWindow.id)} aria-label={`Cerrar ${currentWindow.title}`}>×</button></div>)}<button type="button" className="trafico-close-all-windows" onClick={this.closeAllWindows} title="Cerrar todas las pestañas" aria-label="Cerrar todas las pestañas"><i className="bx bx-x-circle" aria-hidden="true" /></button></div>}
        </>;
    }
}

export default TraficoDesktopWindows;
